import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PhoneOutlined,
  EmailOutlined,
  LocationOnOutlined,
  SendOutlined,
  AccessTimeOutlined,
} from '@mui/icons-material';
import ListPageLayout from '../components/shared/list-page-layout';
import { IContactUsRequest } from '../../model/base/request/contact-us-request';

interface ContactUsComponentProps {
  onSubmit: (data: IContactUsRequest) => Promise<void>;
  entityName?: string;
  entityAddress?: string;
  entityPhone?: string;
  entityEmail?: string;
  entityWorkingHours?: string;
}

export function ContactUsComponent({
  onSubmit,
  entityName,
  entityAddress,
  entityPhone,
  entityEmail,
  entityWorkingHours,
}: ContactUsComponentProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear messages when user starts typing
    if (success) setSuccess(false);
    if (error) setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Note: In production, you would need to implement reCAPTCHA
      // For now, we'll use a placeholder token
      const requestData: IContactUsRequest = {
        ...formData,
        recaptchaToken: 'placeholder-token', // TODO: Implement reCAPTCHA
      };

      await onSubmit(requestData);
      setSuccess(true);
      // Clear form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <LocationOnOutlined sx={{ fontSize: 32 }} />,
      title: 'Endereço',
      content: entityAddress || 'Endereço não disponível',
      show: !!entityAddress,
    },
    {
      icon: <PhoneOutlined sx={{ fontSize: 32 }} />,
      title: 'Telefone',
      content: entityPhone || 'Telefone não disponível',
      show: !!entityPhone,
    },
    {
      icon: <EmailOutlined sx={{ fontSize: 32 }} />,
      title: 'E-mail',
      content: entityEmail || 'E-mail não disponível',
      show: !!entityEmail,
    },
    {
      icon: <AccessTimeOutlined sx={{ fontSize: 32 }} />,
      title: 'Horário de Atendimento',
      content: entityWorkingHours || 'Segunda a Sexta, das 8h às 17h',
      show: true,
    },
  ].filter((info) => info.show);

  return (
    <ListPageLayout
      title="Fale Conosco"
      subtitle={`Entre em contato com ${entityName || 'a Prefeitura'}`}
      breadcrumbs={[{ label: 'Fale Conosco' }]}
    >
      <Grid container spacing={4}>
        {/* Contact Information Cards */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
            Informações de Contato
          </Typography>
          <Grid container spacing={3}>
            {contactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        background: (theme) =>
                          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        margin: '0 auto 16px',
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {info.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {info.content}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
            Envie sua Mensagem
          </Typography>
          <Card elevation={3}>
            <CardContent sx={{ p: 4 }}>
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Mensagem enviada com sucesso! Entraremos em contato em breve.
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Nome Completo"
                      value={formData.name}
                      onChange={handleChange('name')}
                      disabled={loading}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="E-mail"
                      value={formData.email}
                      onChange={handleChange('email')}
                      disabled={loading}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefone"
                      value={formData.phone}
                      onChange={handleChange('phone')}
                      disabled={loading}
                      inputProps={{ maxLength: 20 }}
                      placeholder="(00) 00000-0000"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Assunto"
                      value={formData.subject}
                      onChange={handleChange('subject')}
                      disabled={loading}
                      inputProps={{ maxLength: 200 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={6}
                      label="Mensagem"
                      value={formData.message}
                      onChange={handleChange('message')}
                      disabled={loading}
                      inputProps={{ maxLength: 1000 }}
                      helperText={`${formData.message.length}/1000 caracteres`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendOutlined />}
                        sx={{
                          minWidth: 200,
                          py: 1.5,
                          background: (theme) =>
                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          '&:hover': {
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          },
                        }}
                      >
                        {loading ? 'Enviando...' : 'Enviar Mensagem'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Card elevation={2} sx={{ backgroundColor: '#f5f7fa' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Informações Importantes
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Todas as mensagens são respondidas em até 48 horas úteis.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Para solicitações urgentes, entre em contato através do telefone disponibilizado.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Suas informações pessoais serão tratadas de acordo com nossa Política de Privacidade.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </ListPageLayout>
  );
}
